import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CountUp from '@/components/landing/common/CountUp'
import CodeWindow from '@/components/landing/developers/CodeWindow';

const tabs = ['Python', 'TypeScript', 'cURL'] as const;
const bgs = [
'/18.png',
'1.png',
'/10.png'
]
const names = ['get_filings.py', 'fundExposure.ts', 'prices.sh']
const codes = [
`from axion import Axion
client = Axion(api_key='axn_123')

results = client.filings.search(
  year='2024',
  quarter='1',
  form='4',
  ticker='AAPL'
)
print(results)

`,

`
const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const exposure = await client.etfs.exposure('AAPL');
console.log(exposure); `,

`


curl -X GET "https://api.axionquant.com/stocks/AAPL"
\
-H "x-api-key: your-api-key-here"`
]

type Tab = typeof tabs[number];

const StyledComp = styled.div`
  --bg: #ffffff;
  --text-primary: #0b0b0f;
  --text-secondary: #5b5f6b;
  --text-muted: #9aa0ab;
  --border: #e6e7eb;
  --surface: #f7f7f8;
  --btn-dark-bg: #111114;
  --btn-dark-text: #ffffff;

  color: var(--text-primary);
  padding: 120px 40px;

  .inner {
    max-width: 1400px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 64px;
    align-items: center;
  }

  .left {
    display: flex;
    flex-direction: column;
  }

  h2 {
    line-height: 1.08;
    letter-spacing: -0.01em;
    margin: 0 0 24px 0;
  }

  h2 .muted-line {
    display: block;
    color: var(--text-secondary);
    font-weight: 600;
  }

  .description {
    font-size: 17px;
    line-height: 1.55;
    color: var(--text-secondary);
    max-width: 440px;
    margin: 0 0 36px 0;
  }

  .actions {
    display: flex;
    gap: 12px;
    margin-bottom: 56px;
  }

  .btn {
    font-size: 14px;
    padding: 12px 20px;
    border-radius: 8px;
    cursor: pointer;
    border: 1px solid transparent;
    transition: background 0.3s;
  }

  .btn-primary {
    background: var(--btn-dark-bg);
    color: var(--btn-dark-text);
  }
  .btn-primary:hover {
    background: #333;
  }

  .btn-secondary {
    background: transparent;
    color: var(--text-primary);
  }
  .btn-secondary:hover {
    background: #eee;
  }

  .stats {
    display: flex;
    gap: 40px;
  }

  .stat-number {
    font-size: 22px;
    margin-bottom: 4px;
  }

  .stat-label {
    font-size: 13px;
    color: var(--text-muted);
  }

  .right {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: 8px;
  }

  .code-tabs {
    display: flex;
    gap: 4px;
    padding: 10px 0;
    background: white;
  }

  .code-tab {
    font-size: 13px;
    font-weight: 500;
    padding: 6px 0;
    margin-right: 18px;
    border-radius: 0;
    color: var(--text-muted);
    cursor: pointer;
    border: none;
    background: transparent;
    transition: background 0.15s ease, color 0.15s ease;
  }

  .code-tab.active {
    color: var(--text-primary);
    background: #ffffff;
    border-bottom: 1px solid black;
  }

  .code-placeholder {
    min-height: 320px;
  }

  @media (max-width: 880px) {
    padding: 56px 24px;

    .inner {
      grid-template-columns: 1fr;
      gap: 40px;
    }

    h2 {
      font-size: 38px;
    }
  }
`;

const Comp = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>('Python');

  return (
    <StyledComp>
      <div className="inner">
        <div className="left">
          <h2 className="serif">
            One Python financial data API.
            <span className="muted-line">Every signal.</span>
          </h2>
          <p className="description">
            Pricing, fundamentals, sentiment, news, and alternative metrics - all through one financial data SDK. Start building your trading strategy API in seconds.
          </p>
          <div className="actions">
            <button className="btn btn-primary" onClick={() => { navigate('/dashboard/api-keys') }}>Get API Key</button>
            <button className="btn btn-secondary" onClick={() => { navigate('/docs') }}>Read Docs</button>
          </div>
          <div className="stats">
            <div className="stat">
              <div className="stat-number sans">
                <CountUp from={0}
                  to={1}
                  separator=","
                  direction="up"
                  duration={2}
                  className="count-up-text"
                />

                M+</div>
              <div className="stat-label">API calls per day</div>
            </div>
            <div className="stat">
              <div className="stat-number sans">
                <CountUp from={0}
                  to={90}
                  separator=","
                  direction="up"
                  duration={1}
                  className="count-up-text"
                />
                ms</div>
              <div className="stat-label">Median response time</div>
            </div>
            <div className="stat">
              <div className="stat-number sans">
                <CountUp from={0}
                  to={16}
                  separator=","
                  direction="up"
                  duration={2}
                  className="count-up-text"
                />
                +</div>
              <div className="stat-label">Data classes</div>
            </div>
          </div>
        </div>

        <div className="right">

          <CodeWindow
            filename={names[tabs.indexOf(activeTab)]}
            bg={bgs[tabs.indexOf(activeTab)]}
            code={codes[tabs.indexOf(activeTab)]}
          />

            <div className="code-tabs">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  className={`code-tab ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
      </div>
    </StyledComp>
  );
};

export default Comp;
